import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Input, Select } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';

import GetField from '../Components/GetField';
import styles from '../styles';
import { clearActiveJobchartIds, getActiveJobchartIds } from '../Actions';
import constants from '../constants';
import Api from '../Api';
import { errorNotification } from '../Snackbar';

const AntSelect = GetField(Select);
const AntInput = GetField(Input);
const { Option } = Select;

const FabricForm = props => {
  const {
    classes,
    getValues,
    setValue,
    control,
    watch,
    fabricItem,
    fabricIndex,
    fabricAppend,
    fabricRemove,
  } = props;
  const dispatch = useDispatch();

  const customers = useSelector(state => state.customers);
  const activeJobchartIds = useSelector(state => state.activeJobchartIds);

  const [fabricsTable, setFabricsTable] = useState({
    quantities: {},
    data: {
      productIndexes: {},
      productNames: [],
      fabrics: [],
    },
  });

  const GetFabrics = useCallback(async id => {
    try {
      const { data } = await Api.get(
        `/jobcharts/fabricsfordailycompletion/${id}`
      );

      setFabricsTable(data);
    } catch (error) {
      errorNotification(error.message);
    }
  }, []);

  useEffect(() => {
    const customerId = getValues(`fabrics[${fabricIndex}].customerId`);
    if (!customerId) {
      dispatch(clearActiveJobchartIds());
      setValue('jobchartId', null);
    } else {
      dispatch(getActiveJobchartIds({ customerId }));
    }
  }, [watch(`fabrics[${fabricIndex}].customerId`)]);

  useEffect(() => {
    const jobchartId = getValues(`fabrics[${fabricIndex}].jobchartId`);
    if (!jobchartId) {
      setValue('productId', null);
    } else {
      GetFabrics(jobchartId);
    }
  }, [watch(`fabrics[${fabricIndex}].jobchartId`)]);

  const fabricsArray = useMemo(() => {
    if (fabricsTable.data.productNames.length) {
      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const productIndex = fabricsTable.data.productIndexes[productId];

      if (productIndex !== undefined) {
        return fabricsTable.data.fabrics[productIndex].fabricNames || [];
      }
    }
    return [];
  }, [fabricsTable, watch(`fabrics[${fabricIndex}].productId`)]);

  const widthsArray = useMemo(() => {
    if (fabricsTable.data.fabrics.length) {
      const fabricName = getValues(`fabrics[${fabricIndex}].fabric`);

      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const productIndex = fabricsTable.data.productIndexes[productId];

      if (fabricsTable.data.fabrics[productIndex]?.widths !== undefined) {
        return fabricsTable.data.fabrics[productIndex].widths[fabricName] || [];
      }
    }
    return [];
  }, [
    fabricsTable,
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].fabric`),
  ]);

  const baseColorsArray = useMemo(() => {
    if (fabricsTable.data.fabrics.length) {
      const fabricId = getValues(`fabrics[${fabricIndex}].fabricId`);

      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const productIndex = fabricsTable.data.productIndexes[productId];

      if (fabricsTable.data.fabrics[productIndex]?.baseColors !== undefined) {
        return (
          fabricsTable.data.fabrics[productIndex].baseColors[fabricId] || []
        );
      }
    }
    return [];
  }, [
    fabricsTable,
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].fabricId`),
  ]);

  const printColorsArray = useMemo(() => {
    if (fabricsTable.data.fabrics.length) {
      const fabricId = getValues(`fabrics[${fabricIndex}].fabricId`);

      const productId = getValues(`fabrics[${fabricIndex}].productId`);

      const baseColorId = getValues(`fabrics[${fabricIndex}].baseColorId`);

      const productIndex = fabricsTable.data.productIndexes[productId];

      if (
        fabricsTable.data.fabrics[productIndex]?.printColors[fabricId] !==
        undefined
      ) {
        return (
          fabricsTable.data.fabrics[productIndex].printColors[fabricId][
            baseColorId
          ] || []
        );
      }
    }
    return [];
  }, [
    fabricsTable,
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].fabricId`),
    watch(`fabrics[${fabricIndex}].baseColorId`),
  ]);

  useEffect(() => {
    setValue(
      `fabrics[${fabricIndex}].jobchartQuantity`,
      fabricsTable.quantities[
        `P${watch(`fabrics[${fabricIndex}].productId`)}F${watch(
          `fabrics[${fabricIndex}].fabricId`
        )}B${watch(`fabrics[${fabricIndex}].baseColorId`)}P${
          watch(`fabrics[${fabricIndex}].printColorId`) || ''
        }`
      ] || 0
    );
  }, [
    fabricsTable,
    watch(`fabrics[${fabricIndex}].productId`),
    watch(`fabrics[${fabricIndex}].fabricId`),
    watch(`fabrics[${fabricIndex}].baseColorId`),
    watch(`fabrics[${fabricIndex}].printColorId`),
  ]);

  return (
    <tr>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].customerId`}
          control={control}
          defaultValue={fabricItem.customerId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select customer"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].jobchartId`, null);
                setValue(`fabrics[${fabricIndex}].productId`, null);
                setValue(`fabrics[${fabricIndex}].fabric`, null);
                setValue(`fabrics[${fabricIndex}].fabricId`, null);
                setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
              }}
            >
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.name}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].jobchartId`}
          control={control}
          defaultValue={fabricItem.jobchartId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select bpo"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].productId`, null);
                setValue(`fabrics[${fabricIndex}].fabric`, null);
                setValue(`fabrics[${fabricIndex}].fabricId`, null);
                setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
              }}
            >
              {activeJobchartIds.map(jobchart => (
                <Option key={jobchart.id} value={jobchart.id}>
                  {jobchart.bpoNo}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].productId`}
          control={control}
          defaultValue={fabricItem.productId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select product"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].fabric`, null);
                setValue(`fabrics[${fabricIndex}].fabricId`, null);
                setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
              }}
            >
              {fabricsTable.data.productNames.map(({ product, id }) => (
                <Option key={id} value={id}>
                  {product}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fabric`}
          control={control}
          defaultValue={fabricItem.fabric}
          render={({ field }) => (
            <AntSelect
              placeholder="Select fabric"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].fabricId`, null);
                setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
              }}
            >
              {fabricsArray.map(fabric => (
                <Option key={fabric} value={fabric}>
                  {fabric}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].fabricId`}
          control={control}
          defaultValue={fabricItem.fabricId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select width"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].baseColorId`, null);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
              }}
            >
              {widthsArray.map(width => (
                <Option key={width.id} value={width.id}>
                  {width.width}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].baseColorId`}
          control={control}
          defaultValue={fabricItem.baseColorId}
          render={({ field }) => (
            <AntSelect
              placeholder="Select color"
              {...field}
              onChange={value => {
                field.onChange(value);
                setValue(`fabrics[${fabricIndex}].printColorId`, null);
              }}
            >
              {baseColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].printColorId`}
          control={control}
          defaultValue={fabricItem.printColorId}
          render={({ field }) => (
            <AntSelect placeholder="Select color" {...field}>
              {printColorsArray.map(color => (
                <Option key={color.id} value={color.id}>
                  {color.color}
                </Option>
              ))}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].jobchartQuantity`}
          control={control}
          defaultValue={fabricItem.jobchartQuantity}
          render={({ field }) => (
            <AntInput
              {...field}
              placeholder="Jobchart Qty"
              disabled
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].process`}
          control={control}
          defaultValue={fabricItem.process}
          render={({ field }) => (
            <AntSelect placeholder="Select process" {...field}>
              {Object.values(constants.dailyCompletionProcesses).map(
                process => (
                  <Option key={process} value={process}>
                    {startCase(process)}
                  </Option>
                )
              )}
            </AntSelect>
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].quantity`}
          control={control}
          defaultValue={fabricItem.quantity}
          render={({ field }) => (
            <AntInput
              placeholder="Quantity"
              {...field}
              type="number"
              min={0}
              size="small"
            />
          )}
        />
      </td>
      <td className={classes.inputTd}>
        <Controller
          name={`fabrics[${fabricIndex}].remark`}
          control={control}
          defaultValue={fabricItem.remark}
          render={({ field }) => <AntInput placeholder="Remark" {...field} />}
        />
      </td>
      <td className={classes.inputTd}>
        <DeleteOutlined
          onClick={() => {
            fabricRemove(fabricIndex);
          }}
        />
        &nbsp; &nbsp;
        <PlusCircleOutlined onClick={() => fabricAppend({})} />
      </td>
    </tr>
  );
};

FabricForm.propTypes = {
  classes: PropTypes.object.isRequired,
  getValues: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  control: PropTypes.any.isRequired,
  watch: PropTypes.func.isRequired,
  fabricItem: PropTypes.object.isRequired,
  fabricIndex: PropTypes.number.isRequired,
  fabricAppend: PropTypes.func.isRequired,
  fabricRemove: PropTypes.func.isRequired,
};

export default withStyles(styles)(FabricForm);
